import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

function PosteScanRaspberry({ lecteur }) {
  const [session, setSession] = useState(null);
  const [operateur, setOperateur] = useState(null);
  const [operation, setOperation] = useState(null);
  const [cartesScannees, setCartesScannees] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [message, setMessage] = useState('Scannez une carte opérateur');
  const [messageType, setMessageType] = useState('info');
  const [dernierScan, setDernierScan] = useState(null);

  const inputRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  // Auto-focus permanent sur l'input
  useEffect(() => {
    const focusInput = () => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    };

    focusInput();
    const interval = setInterval(focusInput, 100);
    document.addEventListener('click', focusInput);

    return () => {
      clearInterval(interval);
      document.removeEventListener('click', focusInput);
    };
  }, []);

  const showMessage = (text, type = 'info', duration = 3000) => {
    setMessage(text);
    setMessageType(type);
    if (duration > 0) {
      setTimeout(() => {
        setMessage(getDefaultMessage());
        setMessageType('info');
      }, duration);
    }
  };

  const getDefaultMessage = () => {
    if (!session) return 'Scannez une carte opérateur';
    if (!operation) return 'Scannez une opération';
    return 'Scannez vos cartes';
  };

  // Correction des caractères pour le scanner OBZ (CapsLock activé + AZERTY avec Shift sur chiffres)
  const fixScannerCharacters = (input) => {
    const charMap = {
      // Chiffres avec Shift sur clavier AZERTY français
      '&': '1',  // Shift+1 en AZERTY
      'É': '2',  // Shift+2 en AZERTY
      '"': '3',  // Shift+3 en AZERTY
      '\'': '4', // Shift+4 en AZERTY
      '(': '5',  // Shift+5 en AZERTY
      '-': '6',  // Shift+6 en AZERTY
      'È': '7',  // Shift+7 en AZERTY
      '_': '8',  // Shift+8 en AZERTY
      'Ç': '9',  // Shift+9 en AZERTY
      'À': '0',  // Shift+0 en AZERTY
      
      // Pas besoin de mapper les lettres majuscules en minuscules
      // car les codes-barres utilisent des majuscules
    };
    
    let result = input;
    for (const [wrong, correct] of Object.entries(charMap)) {
      result = result.split(wrong).join(correct);
    }
    console.log('Scan corrigé:', input, '→', result);
    return result;
  };

  const handleInputChange = (value) => {
    setScanInput(value);

    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    scanTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        const correctedValue = fixScannerCharacters(value.trim());
        processScan(correctedValue);
        setScanInput('');
      }
    }, 1000); // 1 seconde pour voir les caractères scannés
  };

  const handleScan = (e) => {
    e.preventDefault();
    const value = scanInput.trim();
    if (value) {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      const correctedValue = fixScannerCharacters(value);
      processScan(correctedValue);
      setScanInput('');
    }
  };

  const processScan = async (codeBarre) => {
    console.log('Scan reçu:', codeBarre);

    // Codes-barres système spéciaux
    if (codeBarre === 'SYS_EXIT_KIOSK' || codeBarre === 'EXITKIOSK') {
      showMessage('Sortie du mode kiosk...', 'info', 1000);
      
      // Fermer la fenêtre après un court délai
      setTimeout(() => {
        // Tenter de fermer la fenêtre
        window.close();
        
        // Si window.close() ne fonctionne pas (certains navigateurs bloquent),
        // rediriger vers about:blank
        setTimeout(() => {
          window.location.href = 'about:blank';
        }, 500);
      }, 1000);
      return;
    }

    if (!session) {
      await handleOperateurScan(codeBarre);
    } else if (codeBarre === operateur?.code_barre) {
      await handleTerminerSession();
    } else if (!operation) {
      await handleOperationScan(codeBarre);
    } else {
      await handleCarteScan(codeBarre);
    }
  };

  const handleOperateurScan = async (codeBarre) => {
    const { data, error } = await supabase
      .from('operateurs')
      .select('*')
      .eq('code_barre', codeBarre)
      .eq('actif', true)
      .single();

    if (error || !data) {
      showMessage(`Carte opérateur non reconnue : ${codeBarre}`, 'error');
      setDernierScan({ type: 'error', message: `Opérateur non reconnu : ${codeBarre}` });
      return;
    }

    const { data: sessionData } = await supabase
      .from('sessions_scan')
      .insert({
        lecteur_id: lecteur.id,
        operateur_id: data.id,
        statut: 'en_cours',
        heure_debut: new Date().toISOString()
      })
      .select()
      .single();

    setOperateur(data);
    setSession(sessionData);
    showMessage(`Bienvenue ${data.prenom}`, 'success', 2000);
    setDernierScan({ type: 'success', message: `Opérateur: ${data.prenom} ${data.nom}` });
  };

  const handleOperationScan = async (codeBarre) => {
    const { data, error } = await supabase
      .from('operations')
      .select('*, types_produits(nom)')
      .eq('code_barre', codeBarre)
      .single();

    if (error || !data) {
      showMessage(`Opération non reconnue : ${codeBarre}`, 'error');
      setDernierScan({ type: 'error', message: `Opération non reconnue : ${codeBarre}` });
      return;
    }

    const { data: habilite } = await supabase
      .rpc('peut_effectuer_operation', {
        p_operateur_id: operateur.id,
        p_operation_id: data.id
      });

    if (!habilite) {
      showMessage('Vous n\'êtes pas habilité pour cette opération', 'error');
      setDernierScan({ type: 'error', message: 'Non habilité pour cette opération' });
      return;
    }

    setOperation(data);
    showMessage('Opération validée', 'success', 2000);
    setDernierScan({ type: 'success', message: `Opération: ${data.nom}` });
  };

  const handleCarteScan = async (codeBarre) => {
    if (cartesScannees.some(c => c.code_barre === codeBarre)) {
      showMessage('Carte déjà scannée', 'error', 2000);
      setDernierScan({ type: 'error', message: 'Carte déjà scannée dans cette session' });
      return;
    }

    const { data: carte, error } = await supabase
      .from('cartes_production')
      .select('*')
      .eq('code_barre', codeBarre)
      .single();

    if (error || !carte) {
      showMessage(`Carte non reconnue : ${codeBarre}`, 'error');
      setDernierScan({ type: 'error', message: `Code-barre non reconnu : ${codeBarre}` });
      return;
    }

    if (carte.type_produit_id !== operation.type_produit_id) {
      showMessage(`Type de produit incorrect (attendu: ${operation.types_produits?.nom})`, 'error');
      setDernierScan({ 
        type: 'error', 
        message: `Type produit incorrect: ${operation.types_produits?.nom} attendu` 
      });
      return;
    }

    // Enregistrer le scan dans scans_cartes_production
    const { error: scanError } = await supabase
      .from('scans_cartes_production')
      .insert([{
        session_id: session.id,
        carte_production_id: carte.id
      }]);

    if (scanError) {
      console.error('Erreur enregistrement scan:', scanError);
      showMessage('Erreur lors de l\'enregistrement du scan', 'error');
      return;
    }

    // Mettre à jour le compteur de la session
    await supabase
      .from('sessions_scan')
      .update({ nombre_cartes_scannees: cartesScannees.length + 1 })
      .eq('id', session.id);

    const nouvellesCartes = [...cartesScannees, carte];
    setCartesScannees(nouvellesCartes);
    showMessage('Scan valide ✓', 'success', 1500);
    setDernierScan({ 
      type: 'success', 
      message: `Carte #${carte.numero_carte} validée` 
    });
  };

  const handleTerminerSession = async () => {
    if (cartesScannees.length === 0) {
      showMessage('Aucune carte scannée', 'error');
      return;
    }

    const now = new Date().toISOString();

    for (const carte of cartesScannees) {
      // Rechercher le suivi existant pour cette carte et cette opération
      const { data: suiviExistant, error: selectError } = await supabase
        .from('suivi_operations')
        .select('*')
        .eq('carte_production_id', carte.id)
        .eq('operation_id', operation.id)
        .single();

      if (suiviExistant) {
        // Mettre à jour le suivi existant
        const updateData = {
          operateur_id: operateur.id,
          validee: true,
          date_fin: now
        };

        // Ajouter date_debut si elle n'existe pas
        if (!suiviExistant.date_debut) {
          updateData.date_debut = now;
        }

        await supabase
          .from('suivi_operations')
          .update(updateData)
          .eq('id', suiviExistant.id);
      } else {
        // Créer un nouveau suivi si il n'existe pas
        await supabase
          .from('suivi_operations')
          .insert([{
            carte_production_id: carte.id,
            operation_id: operation.id,
            operateur_id: operateur.id,
            validee: true,
            date_debut: now,
            date_fin: now
          }]);
      }
    }

    await supabase
      .from('sessions_scan')
      .update({
        statut: 'terminee',
        heure_fin: new Date().toISOString()
      })
      .eq('id', session.id);

    showMessage(`Session terminée - ${cartesScannees.length} cartes validées`, 'success', 4000);

    setTimeout(() => {
      setSession(null);
      setOperateur(null);
      setOperation(null);
      setCartesScannees([]);
      setDernierScan(null);
    }, 3000);
  };

  const getMessageColor = () => {
    switch(messageType) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#3b82f6';
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '4px 15px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Nom du scanner */}
      <div style={{
        textAlign: 'center',
        color: 'white',
        fontSize: '18px',
        fontWeight: '700',
        marginBottom: '5px',
        padding: '0px',
        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
      }}>
        {lecteur?.nom || 'Poste Raspberry'}
      </div>

      {/* Informations session */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '6px',
        padding: '5px',
        marginBottom: '5px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
      }}>        
        {operateur && (
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#212529',
            marginBottom: '2px'
          }}>
            <span style={{ color: '#6c757d' }}>Opérateur:</span> {operateur.prenom} {operateur.nom}
          </div>
        )}
        {!operateur && !operation && (
    <div style={{
      fontSize: '12px',
      fontWeight: '600',
      color: '#6c757d',
      textAlign: 'center'
    }}>
      Informations session
    </div>
  )}
        {operation && (
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#212529'
          }}>
            <span style={{ color: '#6c757d' }}>Opération:</span> {operation.types_produits?.nom} - Etape {operation.ordre} - {operation.nom}
          </div>
        )}
      </div>

      {/* Message principal */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '6px',
        padding: '5px',
        marginBottom: '5px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '700',
          color: getMessageColor(),
          marginBottom: '0px'
        }}>
          {message}
        </div>

        {/* Input caché mais actif */}
        <form onSubmit={handleScan}>
          <input
            ref={inputRef}
            type="text"
            value={scanInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (scanTimeoutRef.current) {
                  clearTimeout(scanTimeoutRef.current);
                }
                handleScan(e);
              }
            }}
            autoFocus
            autoComplete="off"
            style={{
              width: '65%',
              padding: '2px',
              border: '1px solid #667eea',
              borderRadius: '4px',
              fontSize: '10px',
              textAlign: 'center',
              outline: 'none'
            }}
          />
        </form>
      </div>

      {/* Feedback dernier scan */}
      {dernierScan && (
        <div style={{
          backgroundColor: dernierScan.type === 'success' ? '#d1fae5' : '#fee2e2',
          border: `2px solid ${dernierScan.type === 'success' ? '#10b981' : '#ef4444'}`,
          borderRadius: '6px',
          padding: '5px',
          marginBottom: '5px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: dernierScan.type === 'success' ? '#10b981' : '#ef4444',
            marginBottom: '2px'
          }}>
            {dernierScan.type === 'success' ? 'Scan valide ✓' : 'Scan non valide ✗'}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#495057'
          }}>
            {dernierScan.message}
          </div>
        </div>
      )}

      {/* Compteur */}
      {session && operation && (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '6px',
          padding: '4px',
          marginBottom: '5px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '900',
            color: '#667eea',
            marginBottom: '4px',
            lineHeight: '1'
          }}>
            {cartesScannees.length}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6c757d',
            fontWeight: '600'
          }}>
            {cartesScannees.length <= 1 ? 'carte scannée' : 'cartes scannées'}
          </div>
        </div>
      )}

      {/* Bouton Valider la session */}
      {session && operation && cartesScannees.length > 0 && (
        <button
          onClick={handleTerminerSession}
          style={{
            padding: '5px',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '700',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)',
            marginBottom: '5px'
          }}
        >
          ✓ VALIDER LA SESSION
        </button>
      )}
    </div>
  );
}

export default PosteScanRaspberry;
